/**
 * What the views hold while one works – a search, a filter, a form not saved yet, an open part –
 * kept while the app runs, so going elsewhere and coming back finds it as it was (Martin,
 * 23.10.2026: „wenn ich zurück navigiere, fange ich von vorne an“). For one business: another one
 * starts empty. Also where each view was scrolled to.
 */
import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

const kept = new Map<string, unknown>();
let workspace = "";

/** Another business: nothing of the one before. */
export function keptFor(id: string) {
  if (id === workspace) return;
  workspace = id;
  kept.clear();
}

/** A value set for a view before it opens (a link that opens a menu in a language). */
export const keep = (key: string, value: unknown) => void kept.set(key, value);

/** As useState, but the value stays under `key` when the view goes and comes back. */
export function useKept<T>(key: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => (kept.has(key) ? (kept.get(key) as T) : typeof initial === "function" ? (initial as () => T)() : initial));
  useEffect(() => void kept.set(key, value), [key, value]);
  return [value, setValue];
}

/**
 * A draft of something saved on the server (a form, a list): kept with the state it began from;
 * when that changed meanwhile (saved here or elsewhere), the draft begins anew from it.
 */
export function useDraft<T>(key: string, base: string, fresh: () => T): [T, Dispatch<SetStateAction<T>>] {
  const [held, setHeld] = useKept<{ base: string; value: T }>(key, () => ({ base, value: fresh() }));
  useEffect(() => {
    if (held.base !== base) setHeld({ base, value: fresh() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);
  const value = held.base === base ? held.value : fresh();
  const set: Dispatch<SetStateAction<T>> = (next) => setHeld((h) => ({ base, value: typeof next === "function" ? (next as (p: T) => T)(h.base === base ? h.value : fresh()) : next }));
  return [value, set];
}

/** Forgets a draft, after it was saved or thrown away. */
export const forget = (key: string) => void kept.delete(key);

// --- where each view was scrolled to -----------------------------------------

const scrolled = new Map<string, number>();
/** Keeps how far the view at `hash` is scrolled, as one leaves it. */
export const keepScroll = (hash: string) => void scrolled.set(hash, window.scrollY);
/** Back where the view at `hash` was, once it has drawn. */
export function restoreScroll(hash: string) {
  const y = scrolled.get(hash) ?? 0;
  requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: y })));
}
